import { useState, MutableRefObject } from "react";
import { FPBand } from "../../lib/fpBands";
import { fpBandAccent, FPBandPill } from "./FPBandDivider";

interface Props {
  index: number;
  total: number;
  label: string;
  band: FPBand;
  dragIndexRef: MutableRefObject<number | null>;
  move: (from: number, to: number) => void;
  isLast: boolean;
  /** Adds ▲/▼ reorder buttons alongside the drag handle. HTML5 drag-and-drop (this row's
   * `draggable`) has no touch equivalent, so without these a mobile member's only way to
   * reorder is the cramped "#" jump-to-position input. Defaults to false for the one
   * admin-only caller (FinalPositionsTablePicker) that doesn't pass it. */
  isMobile?: boolean;
}

/**
 * One row of a Final Positions drag-to-rank list: drag handle, position number, club name,
 * band pill, and a "#" control to jump straight to a position instead of dragging. Used by
 * every FP prediction-editing screen so they look and behave identically.
 */
export default function FpDragRow({ index, total, label, band, dragIndexRef, move, isLast, isMobile = false }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(index + 1));
  const accent = fpBandAccent(band);

  const commit = () => {
    const target = parseInt(value, 10);
    if (!Number.isNaN(target) && target >= 1 && target <= total && target - 1 !== index) {
      move(index, target - 1);
    }
    setEditing(false);
  };

  return (
    <div
      draggable
      onDragStart={() => { dragIndexRef.current = index; }}
      onDragOver={(e) => {
        e.preventDefault();
        if (dragIndexRef.current === null || dragIndexRef.current === index) return;
        move(dragIndexRef.current, index);
        dragIndexRef.current = index;
      }}
      onDragEnd={() => { dragIndexRef.current = null; }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.75rem 1rem",
        borderBottom: isLast ? "none" : "1px solid var(--color-border-subtle)",
        borderLeft: `4px solid ${accent.color}`,
        cursor: "grab",
        userSelect: "none",
        background: "var(--color-surface)",
      }}
    >
      <span style={{ color: "var(--color-text-dim)", flexShrink: 0, fontSize: "0.95rem", cursor: "grab", lineHeight: 1 }}>☰</span>
      <span style={{ width: "1.7rem", textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--color-text-dim)", flexShrink: 0 }}>
        {index + 1}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: "0.92rem", fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <FPBandPill band={band} />
      {isMobile && (
        <div style={{ display: "flex", gap: "0.3rem", flexShrink: 0 }}>
          <button
            onClick={() => move(index, index - 1)}
            disabled={index === 0}
            title="Move up"
            style={{
              width: 36, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center",
              background: "none", border: "1px solid var(--color-border)", borderRadius: 6,
              fontSize: "0.85rem", cursor: index === 0 ? "not-allowed" : "pointer",
              color: index === 0 ? "var(--color-text-dim)" : "var(--color-text-muted)",
              opacity: index === 0 ? 0.4 : 1,
            }}
          >
            ▲
          </button>
          <button
            onClick={() => move(index, index + 1)}
            disabled={index === total - 1}
            title="Move down"
            style={{
              width: 36, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center",
              background: "none", border: "1px solid var(--color-border)", borderRadius: 6,
              fontSize: "0.85rem", cursor: index === total - 1 ? "not-allowed" : "pointer",
              color: index === total - 1 ? "var(--color-text-dim)" : "var(--color-text-muted)",
              opacity: index === total - 1 ? 0.4 : 1,
            }}
          >
            ▼
          </button>
        </div>
      )}
      {editing ? (
        <input
          type="number"
          min={1}
          max={total}
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          onBlur={commit}
          style={{ width: "2.6rem", fontSize: "0.78rem", padding: "0.25rem", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-surface-alt)", color: "var(--color-text)", textAlign: "center", flexShrink: 0 }}
        />
      ) : (
        <button
          onClick={() => { setValue(String(index + 1)); setEditing(true); }}
          title="Jump to position"
          style={{
            background: "none", border: "1px solid var(--color-border)", borderRadius: 6,
            padding: isMobile ? "0.32rem 0.7rem" : "0.32rem 0.5rem", minHeight: isMobile ? 44 : undefined,
            fontSize: "0.72rem", cursor: "pointer", color: "var(--color-text-muted)", flexShrink: 0, lineHeight: 1,
          }}
        >
          #
        </button>
      )}
    </div>
  );
}
